import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { lookup } from '@npc/copy'
import { t } from '../../copy'
import { useTeam, useTeamMe } from '../../app/queries'
import { useModelProfiles } from '../models/model-library'
import type { DerivedMention } from './composer'
import { humanAskedForWork, replyAskedForProject } from './intent'
import type { Effort } from './model-picker'
import {
  awaitReplyBudgetMs,
  isWorkingLine,
  replyArrived,
  shouldAwaitReply,
  thinkingSecondsOf,
} from './rail'
import {
  addConversationPerson,
  answerDecision,
  bindConversationProject,
  cancelConversationRun,
  cancelLuluSubrun,
  joinConversationTeam,
  pauseConversation,
  recoverSubrun,
  removeConversationPerson,
  renameConversation,
  resumeConversation,
  scheduleDevelopment,
  sendLuluMessage,
  setComposerModel,
  undoScheduledTask,
  useComposerModel,
  useConversationPlan,
  useLuluAgents,
  useLuluConversation,
  useLuluMessages,
  useLuluParticipants,
  useLuluSkills,
  usePendingDecisions,
  useWorkboard,
} from './queries'
import { buildSnapshot } from './snapshot'
import { CancelRunKeys } from './cancel-run-key'
import { RecoveryKeys, isDefinitiveRejection } from './recovery-key'
import { useConversationAttachments } from './use-attachments'
import { useConversationStream } from './use-stream'
import { useStreamProjectionRefresh } from './terminal-refresh'
import { currentPlan, peopleFromWorkboard, teamFromRoster, workboardToNodes } from './wire'

/**
 * 一条会话的全部接线：取数、投影、动作。
 *
 * ------------------------------------------------------------------
 * # 为什么抽出来
 *
 * 2026-09-08 arron：「项目里的会话，里面的团队要和外面 Lulu 对话保持一致」。
 *
 * 在这之前 /lulu（index.tsx）与项目页的对话模块（project/modules/conversation.tsx）
 * 各接了一份。第二份是照着第一份抄的，抄的那天两份一样；之后每修一处，
 * 另一处就少一处——到这一天已经分家三处，而没有任何东西会红：
 *
 *   只 @ 人不进等待态（L-63）      项目页没有，@ 一个人之后「一直在思考」
 *   revision 重取六份投影（L-71）  项目页只刷三份，审批到了那一栏不亮
 *   Sidecar                          项目页把 onOpenSidecar 写成空函数，
 *                                    「团队」那颗按钮点了没反应
 *
 * 设计稿 pjChat 的原话是「和公共 Lulu 是同一个内核，只是把项目固定住了」。
 * 「同一个内核」不能靠两份代码各自小心，只能靠**一份代码**。
 *
 * # 这里有什么、没有什么
 *
 * 有：与**这一条会话**有关的一切——消息、参与方、工作板、名册、审批、
 * 计划、模型、附件、事件流，以及对它们的每一个动作。
 *
 * 没有：会话列表、路由、启动器、新建项目的三阶段、Sidecar 的开合。
 * 那些是「这一屏」的事，主屏与项目页各不相同，各自留在各自的接线层里。
 *
 * # 两处的差别只剩两个参数
 *
 *   agentScope  主屏看账户级 Agent 库；项目页看账户级 + 这个项目的
 *   onRenamed   改名之后各自的列表要重取（主屏两份、项目页一份）
 *
 * 再多一个差别，先问它是不是真的属于「这一屏」。
 * ------------------------------------------------------------------
 */
export interface ConversationWorkbenchOptions {
  readonly conversationId: string | undefined
  /** Agent 库按哪一层看。项目 scope 服务端会把账户级也带上。 */
  readonly agentScope:
    | { readonly scope: 'account' }
    | { readonly scope: 'project'; readonly projectId: string }
  /** 会话改名之后调用方那侧的列表要跟着重取。 */
  readonly onRenamed?: () => Promise<unknown>
  /**
   * 这一轮 Lulu 的回话到了。主屏借它重取项目列表——Lulu 有工具，
   * 其中一件（create_project）真的会往库里写一行，而项目列表的 staleTime 是 30 秒。
   */
  readonly onReplyArrived?: () => void
}

/** 一个会自己走的「现在」：presence 降级与「跑了多久」都要每秒跳一下。 */
export function useTickingNow(everyMs = 1000): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), everyMs)
    return () => clearInterval(id)
  }, [everyMs])
  return now
}

export function useConversationWorkbench({
  conversationId: activeId,
  agentScope,
  onRenamed,
  onReplyArrived,
}: ConversationWorkbenchOptions) {
  const now = useTickingNow()
  const conversation = useLuluConversation(activeId)
  const skills = useLuluSkills()
  const agents = useLuluAgents(
    agentScope.scope,
    agentScope.scope === 'project' ? agentScope.projectId : undefined,
  )
  const modelProfiles = useModelProfiles(conversation.data?.conversation.project_id)
  const board = useWorkboard(activeId)
  const decisions = usePendingDecisions()
  const composerModel = useComposerModel(activeId)
  const stream = useConversationStream(activeId ?? null)

  /**
   * 「一起管」的名册与「我能干什么」。
   *
   * **能不能叫人进来看 capabilities 里有没有 manage_team，不看 role**——
   * 权限规则有两份的那天，UI 上能点的和后端认的就对不上了。
   */
  const team = useTeam()
  const teamMe = useTeamMe()
  const canManageTeam = (teamMe.data?.capabilities ?? []).includes('manage_team')
  const people = board.data === undefined ? undefined : peopleFromWorkboard(board.data)
  const invitablePeople = useMemo(() => {
    if (team.data === undefined || people === undefined) return undefined
    const here = new Set(people.map((p) => p.id))
    return (team.data.items ?? [])
      .filter((m) => !here.has(m.user_id))
      .map((m) => ({
        userId: m.user_id,
        // 名字空着用邮箱：**这条会话里有他**这件事不该因为名字不好看而说不出口。
        label: (m.name ?? '').trim() !== '' ? (m.name as string) : m.email,
        role:
          m.role === 'owner' || m.role === 'operator' || m.role === 'viewer' ? m.role : null,
      }))
  }, [team.data, people])

  // 输入框的「下一条消息模型」直接读统一模型库，绝不从 Agent 成员反推。
  const agentModelOptions = modelProfiles.data
    ? modelProfiles.data.profiles
        .filter(
          (profile) =>
            profile.enabled &&
            profile.status === 'available' &&
            profile.connection_status === 'verified',
        )
        .map((profile) => ({
          id: profile.id,
          name: profile.display_name,
          status: 'ok' as const,
          reason: '',
        }))
    : null

  const recoveryKeys = useRef(new RecoveryKeys())
  const cancelRunKeys = useRef(new CancelRunKeys())

  const archived = conversation.data?.conversation.state === 'archived'
  // 刚发完话、还没等到回音的那几秒把轮询调快。到了就关掉，或者超时兜底关掉。
  const [awaitingReply, setAwaitingReply] = useState(false)
  // 他按下发送的那一刻。**用来算真实秒数**，不是用来做进度条。
  const [awaitingSince, setAwaitingSince] = useState<number | null>(null)
  const awaitingAfter = useRef<{ sequence: number; mine: string | undefined; effort: Effort | null }>(
    { sequence: 0, mine: undefined, effort: null },
  )
  // 等了多少秒。**没在等就是 null**，不是 0——0 会让那一行一直挂着。
  const thinkingSeconds = useMemo(
    () => thinkingSecondsOf(awaitingReply ? awaitingSince : null, now.getTime()),
    [awaitingReply, awaitingSince, now],
  )

  const messages = useLuluMessages(activeId, archived, awaitingReply)
  const participants = useLuluParticipants(activeId)
  const conversationProjectId = conversation.data?.conversation.project_id ?? null
  const goalPlan = useConversationPlan(activeId, conversationProjectId)

  // **流上一有动静就刷新六份投影。** 消息本身现在也走全站流推（L-74），
  // 这里是会话那根流的那一半：执行过程有新事实时，状态、看板、名册、审批、
  // 计划要一起动，否则已打开的页面会拿旧 active/disconnected 盖住终态。
  useStreamProjectionRefresh({
    conversationId: activeId,
    revision: stream.revision,
    // 显式抛出，terminal-refresh 的 allSettled 才能只重试失败投影并最终自愈。
    messages: () => messages.refetch({ throwOnError: true }),
    conversation: () => conversation.refetch({ throwOnError: true }),
    workboard: () => board.refetch({ throwOnError: true }),
    participants: () => participants.refetch({ throwOnError: true }),
    decisions: () => decisions.refetch({ throwOnError: true }),
    plan: () => goalPlan.refetch({ throwOnError: true }),
  })

  const participantsById = useMemo(() => {
    const map = new Map<string, (typeof rows)[number]>()
    const rows = participants.data?.participants ?? []
    for (const row of rows) map.set(row.id, row)
    return map
  }, [participants.data?.participants])

  // 事件流断了 = 撤掉所有「进行中」。两个来源都算：取数失败说的是
  // 「这一次请求没成」，SSE 的 readyState 说的是「此刻还有没有人在给我事实」。
  // 会话还没选出来时 stream.link 恒为 down，那一档不该算断线。
  const streamDown = activeId !== undefined && stream.link === 'down' && stream.opened
  const transportFailed = Boolean(conversation.error) || Boolean(messages.error) || streamDown

  const snapshot = useMemo(
    () =>
      buildSnapshot({
        conversation: conversation.data?.conversation ?? null,
        // 12 态里的 8 档只能从工作板来：Conversation.state 只有四档。
        workboard: board.data ?? null,
        transportFailed,
      }),
    [conversation.data?.conversation, board.data, transportFailed],
  )

  // 团队是**工作板 session 的投影**，不是 Agent 库。两条数据都要读到才画得出来：
  // 缺任一条就是「读不到」，不拿其中一条凑合出一个半真的团队。
  const roster = useMemo(
    () =>
      agents.data === undefined || board.data === undefined
        ? undefined
        : teamFromRoster(agents.data.agents, board.data, now),
    [agents.data, board.data, now],
  )
  const teamMembers = agents.isError || board.isError ? null : roster?.members
  const teamCandidates = roster?.candidates
  const plan = board.data === undefined ? undefined : currentPlan(board.data)
  const boardNodes = board.isError
    ? null
    : board.data === undefined
      ? undefined
      : workboardToNodes(board.data, now)
  const pendingDecisions = decisions.isError
    ? null
    : decisions.data === undefined
      ? undefined
      : decisions.data.decisions

  // 绑定卡要不要在发送之后继续摆着。两个判据取或，理由见 index.tsx 那段历史：
  // humanAskedForWork 看他最后那句在不在提活，replyAskedForProject 看 Lulu 最后
  // 一句是不是那条模板——模型挂了回落到模板的那一轮只有后者成立。
  const askedForProject =
    humanAskedForWork(
      (messages.data?.messages ?? []).map((m) => ({
        body: m.body,
        isHuman: participantsById.get(m.participant_id)?.kind === 'human',
      })),
    ) ||
    replyAskedForProject(messages.data?.messages ?? [], t('steward.smalltalk.work_needs_project'))

  // 会话附件。换会话时自己清干净、进页面时接管服务端留着的那些。
  const attach = useConversationAttachments(activeId)
  const filePickerRef = useRef<HTMLInputElement | null>(null)

  // ── 「开始做」 ────────────────────────────────────────────────
  const [startingWork, setStartingWork] = useState(false)
  const [startWorkFailed, setStartWorkFailed] = useState(false)
  // 刚点完「开始做」拿到的 task_id。**换会话时清掉**。
  const [justScheduledTaskId, setJustScheduledTaskId] = useState<string | null>(null)
  useEffect(() => {
    setJustScheduledTaskId(null)
    setStartWorkFailed(false)
  }, [activeId])
  const startWork = useCallback(() => {
    if (activeId === undefined) return
    setStartWorkFailed(false)
    setStartingWork(true)
    void (async () => {
      try {
        const { taskId } = await scheduleDevelopment(activeId)
        // **先把按钮关掉，再去刷。** 那句「安排下去了」只说几秒（plan.ts 那段）。
        setJustScheduledTaskId(taskId)
        window.setTimeout(() => setJustScheduledTaskId((cur) => (cur === taskId ? null : cur)), 6000)
        // **三样一起刷**：plan（主按钮要按不动）、board（这次安排的产物）、
        // messages（服务端会往会话里写一句回执）。
        await Promise.all([goalPlan.refetch(), board.refetch(), messages.refetch()])
      } catch {
        // 留在原地说清楚，不跳转。重试就是再点一次。
        setStartWorkFailed(true)
      } finally {
        setStartingWork(false)
      }
    })()
  }, [activeId, goalPlan, board, messages])

  const undoTask = useCallback(
    async (taskId: string) => {
      await undoScheduledTask(taskId)
      void board.refetch()
    },
    [board],
  )

  const recover = useCallback(
    async (subrunId: string, kind: 'retry' | 'swap_model' | 'takeover', adapter?: string) => {
      // **同一次意图复用同一把钥匙**（recovery-key.ts）。
      const intent = { subrunId, action: kind, adapter }
      const key = recoveryKeys.current.keyFor(intent)
      try {
        await recoverSubrun({ subrunId, action: kind, runtimeAdapter: adapter, idempotencyKey: key })
        recoveryKeys.current.release(intent)
      } catch (err) {
        // 明确的业务拒绝（4xx）也放；网络错误与 5xx 不放。
        const status = (err as { status?: number } | null)?.status
        if (isDefinitiveRejection(status)) recoveryKeys.current.release(intent)
        throw err
      } finally {
        await board.refetch()
      }
    },
    [board],
  )

  const cancelSubrun = useCallback(
    async (subrunId: string) => {
      try {
        await cancelLuluSubrun(subrunId)
      } finally {
        await board.refetch()
      }
    },
    [board],
  )

  const pause = useCallback(async () => {
    if (!activeId) return
    await pauseConversation(activeId)
    await conversation.refetch()
  }, [activeId, conversation])
  const resume = useCallback(async () => {
    if (!activeId) return
    await resumeConversation(activeId)
    await conversation.refetch()
  }, [activeId, conversation])

  /** 停下这一轮。钥匙的两条规矩见 cancel-run-key.ts。板、计划、消息三样一起刷。 */
  const stopRound = useCallback(
    async (scope: 'all' | 'turn') => {
      if (!activeId) throw new Error('no active conversation')
      const key = cancelRunKeys.current.keyFor(activeId)
      try {
        const out = await cancelConversationRun({ conversationId: activeId, idempotencyKey: key, scope })
        cancelRunKeys.current.release(activeId)
        return { cancelledItems: out.cancelledItems }
      } catch (err) {
        const status = (err as { status?: number } | null)?.status
        if (isDefinitiveRejection(status)) cancelRunKeys.current.release(activeId)
        throw err
      } finally {
        setAwaitingReply(false)
        setAwaitingSince(null)
        await Promise.all([board.refetch(), goalPlan.refetch(), messages.refetch()])
      }
    },
    [activeId, board, goalPlan, messages],
  )

  /** 回答一个红级决定：真 POST 并刷新待决定列表。 */
  const answer = useCallback(
    async (decisionId: string, approve: boolean) => {
      await answerDecision({ decisionId, approve })
      await decisions.refetch()
    },
    [decisions],
  )

  /** 选模型：真 POST 并刷新，让服务端那份成为唯一真相。 */
  const pickModel = useCallback(
    async (policy: 'auto' | 'fixed', profileId: string | undefined) => {
      if (activeId === undefined) return
      await setComposerModel({ conversationId: activeId, policy, profileId, idempotencyKey: crypto.randomUUID() })
      await composerModel.refetch()
    },
    [activeId, composerModel],
  )

  const renamedRef = useRef(onRenamed)
  renamedRef.current = onRenamed
  /** 「/改名 新名字」。这条会话与调用方那侧的列表都重取——只改一处是「改了一半」。 */
  const rename = useCallback(
    async (title: string) => {
      if (activeId === undefined) throw new Error('no conversation')
      await renameConversation(activeId, title)
      await Promise.all([conversation.refetch(), renamedRef.current?.()])
    },
    [activeId, conversation],
  )

  /** 绑到一个项目。绑完重取这条会话：标题栏那句是从服务端读的。 */
  const bindProject = useCallback(
    async (projectId: string) => {
      if (activeId === undefined) return
      await bindConversationProject({ conversationId: activeId, projectId })
      await conversation.refetch()
    },
    [activeId, conversation],
  )

  // **按 capabilities 里有没有 manage_team 决定给不给这些动作**，不按 role 自己推。
  // 名单永远等服务端那份重取，不本地塞一行。
  const invitePerson = useMemo(
    () =>
      canManageTeam && activeId !== undefined
        ? async (userId: string) => {
            await addConversationPerson({ conversationId: activeId, userId })
            await board.refetch()
          }
        : undefined,
    [canManageTeam, activeId, board],
  )
  const removePerson = useMemo(
    () =>
      canManageTeam && activeId !== undefined
        ? async (userId: string) => {
            await removeConversationPerson({ conversationId: activeId, userId })
            await board.refetch()
          }
        : undefined,
    [canManageTeam, activeId, board],
  )
  const joinTeam = useCallback(
    async (agentProfileId: string) => {
      if (activeId === undefined) return
      await joinConversationTeam({ conversationId: activeId, agentProfileId })
      // 团队那一栏是**工作板 session 的投影**，不是本地往列表里塞一行。
      await Promise.all([agents.refetch(), board.refetch()])
    },
    [activeId, agents, board],
  )
  const retrySidecar = useCallback(() => {
    void agents.refetch()
    void board.refetch()
    void decisions.refetch()
  }, [agents, board, decisions])

  /**
   * 关掉那个快轮询。两个条件缺一不可：**等到了**（我刚发那条之后出现一条
   * 不是我发的、而且不是进度语），或者**兜底超时**——一个要靠「等到了」
   * 才停的循环，会在等不到的那天永远跑着。
   */
  const replyArrivedRef = useRef(onReplyArrived)
  replyArrivedRef.current = onReplyArrived
  useEffect(() => {
    if (!awaitingReply) return
    const list = messages.data?.messages ?? []
    if (
      replyArrived(list, awaitingAfter.current.sequence, awaitingAfter.current.mine, (body) =>
        isWorkingLine(body, lookup),
      )
    ) {
      setAwaitingReply(false)
      setAwaitingSince(null)
      replyArrivedRef.current?.()
      return
    }
    const id = setTimeout(() => {
      setAwaitingReply(false)
      setAwaitingSince(null)
    }, awaitReplyBudgetMs(awaitingAfter.current.effort))
    return () => clearTimeout(id)
  }, [awaitingReply, messages.data])

  /**
   * 真发一条消息。幂等键**每次点击现生成一把**。
   */
  const sendMessage = useCallback(
    async (
      body: string,
      attachmentIDs: readonly string[],
      effort: Effort | null,
      planOnly: boolean,
      mentions: readonly DerivedMention[],
    ) => {
      if (activeId === undefined) throw new Error('no conversation')
      const sent = await sendLuluMessage({
        conversationId: activeId,
        body,
        idempotencyKey: crypto.randomUUID(),
        attachmentIDs,
        noAutoStart: planOnly,
        // **这一行是「@ 谁谁答」的最后一节。** 不带的话回话循环看不见他 @ 了谁。
        mentions,
        // 没选就不发这个字段：服务端按功能位默认。
        reasoningEffort: effort ?? undefined,
      })
      awaitingAfter.current = { sequence: sent.message.sequence, mine: sent.message.participant_id, effort }
      // **只清被带走的那些。** 清全部会把一个刚失败、他正打算重试的文件也一起抹掉。
      attach.clearSent(attachmentIDs)
      // 「本次消息模型」由服务端在消息事务内原子消费。浏览器只刷新结果。
      await composerModel.refetch()
      // 发成功之后拉一次让它出现在流里。**不本地插一条假的**。
      await messages.refetch()
      // **只 @ 了人、没 @ AI 的那条不进等待态**（rail.ts shouldAwaitReply）：
      // Lulu 对那种消息是沉默的（L-63），进了等待态就只能等到超时。
      if (shouldAwaitReply(mentions)) {
        setAwaitingReply(true)
        setAwaitingSince(Date.now())
      }
    },
    [activeId, composerModel, messages, attach],
  )

  return {
    now,
    conversation,
    archived,
    paused: conversation.data?.conversation.paused_at != null,
    messages,
    participants,
    participantsById,
    board,
    agents,
    decisions,
    composerModel,
    goalPlan,
    skills,
    stream,
    snapshot,
    transportFailed,
    thinkingSeconds,
    team: teamMembers,
    teamCandidates,
    people,
    invitablePeople,
    plan,
    boardNodes,
    pendingDecisions,
    agentModelOptions,
    askedForProject,
    attach,
    filePickerRef,
    startWork,
    startingWork,
    startWorkFailed,
    justScheduledTaskId,
    undoTask,
    recover,
    cancelSubrun,
    pause,
    resume,
    stopRound,
    answer,
    pickModel,
    rename,
    bindProject,
    invitePerson,
    removePerson,
    joinTeam,
    retrySidecar,
    sendMessage,
  }
}

export type ConversationWorkbench = ReturnType<typeof useConversationWorkbench>
